import React, { useState, useEffect } from "react";
import { addRoom, getRoomTypes } from "../../apis/room";

type AddRoomProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

interface RoomType {
  id: number;
  name: string;
}

type RoomErrors = {
  name?: string;
  floor?: string;
  room_type_id?: string;
};

const AddRoom: React.FC<AddRoomProps> = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    floor: "",
    room_type_id: "",
    status: "available",
  });

  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [errors, setErrors] = useState<RoomErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) fetchRoomTypes();
  }, [open]);

  const fetchRoomTypes = async () => {
    try {
      const res: any = await getRoomTypes();
      setRoomTypes(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch room types", err);
    }
  };

  if (!open) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({ ...prev, [name as keyof RoomErrors]: "" }));
  };

  const validateForm = () => {
    const e: RoomErrors = {};
    if (!formData.name.trim()) e.name = "* Room name is required";
    if (!formData.floor) e.floor = "* Floor is required";
    if (!formData.room_type_id) e.room_type_id = "* Room type is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await addRoom({
        name: formData.name.trim(),
        floor: Number(formData.floor),
        room_type_id: Number(formData.room_type_id),
        status: formData.status,
      });

      handleClose(); 
      onSuccess?.();
    } catch (err: any) {
      if (err.response?.data?.errors) {
        const apiErrors: RoomErrors = {};
        Object.entries(err.response.data.errors).forEach(([key, val]: any) => {
          apiErrors[key as keyof RoomErrors] = val[0];
        });
        setErrors(apiErrors);
      } else {
        setErrors({ name: "Failed to add room" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: "", floor: "", room_type_id: "", status: "available" });
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-primary rounded-t-lg">
          <h2 className="text-white text-base font-semibold">Add Room</h2>
          <button
            onClick={handleClose}
            type="button"
            className="text-white text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Room Name */}
          <div>
            <label className="block text-sm font-medium text-gray-900">Room Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
            {errors.name && <span className="text-sm text-red-500">{errors.name}</span>}
          </div>

          {/* Floor */}
          <div>
            <label className="block text-sm font-medium text-gray-900">Floor</label>
            <input
              type="number"
              name="floor"
              value={formData.floor}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
            {errors.floor && <span className="text-sm text-red-500">{errors.floor}</span>}
          </div>

          {/* Room Type */}
          <div>
            <label className="block text-sm font-medium text-gray-900">Room Type</label>
            <select
              name="room_type_id"
              value={formData.room_type_id}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            >
              <option value="">Select Room Type</option>
              {roomTypes.map((type) => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
            {errors.room_type_id && <span className="text-sm text-red-500">{errors.room_type_id}</span>}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-900">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            >
              <option value="available">Available</option>
              <option value="occupied">Occupied</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 mt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-3 py-2 bg-primary hover:bg-hover text-white rounded disabled:opacity-50"
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="text-sm font-semibold leading-6 text-gray-900"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRoom;